import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { StatusBadge } from "./StatusBadge";

export type ConfidenceLevel = "alta" | "media" | "baixa";

const LEVEL_LABEL: Record<ConfidenceLevel, string> = { alta: "alta", media: "média", baixa: "baixa" };

/** Normaliza os três vocabulários de confiança (0-1, 0-100, alta/média/baixa). */
export function toConfidenceLevel(value: number | string | null | undefined): ConfidenceLevel | null {
  if (value == null) return null;
  if (typeof value === "string") {
    const normalized = value.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
    if (normalized.startsWith("alt") || normalized === "high") return "alta";
    if (normalized.startsWith("med") || normalized === "medium") return "media";
    if (normalized.startsWith("baix") || normalized === "low") return "baixa";
    return null;
  }
  const ratio = value > 1 ? value / 100 : value;
  if (ratio >= 0.75) return "alta";
  if (ratio >= 0.5) return "media";
  return "baixa";
}

interface AIBadgeProps {
  label?: string;
  confidence?: number | string | null;
  className?: string;
}

/**
 * Marca única de conteúdo gerado ou sugerido por IA. O ícone Sparkles fica
 * reservado para isso ("Vale abordar" e afins usam outro ícone).
 */
export function AIBadge({ label = "IA", confidence, className }: AIBadgeProps) {
  const level = toConfidenceLevel(confidence);
  return (
    <StatusBadge tone="ai" icon={Sparkles} className={className}>
      {label}
      {level === "baixa" && <span className="font-normal opacity-80">· confiança baixa</span>}
    </StatusBadge>
  );
}

/** Mostra confiança só quando ela pede atenção (baixa), ou sempre com `always`. */
export function Confidence({ value, always = false, className }: { value: number | string | null | undefined; always?: boolean; className?: string }) {
  const level = toConfidenceLevel(value);
  if (!level || (level !== "baixa" && !always)) return null;
  return (
    <StatusBadge tone={level === "baixa" ? "warning" : "neutral"} className={className}>
      confiança {LEVEL_LABEL[level]}
    </StatusBadge>
  );
}

interface AISuggestionProps {
  title: React.ReactNode;
  evidence?: React.ReactNode;
  confidence?: number | string | null;
  onAccept?: () => void;
  onEdit?: () => void;
  onReject?: () => void;
  acceptLabel?: string;
  busy?: boolean;
  className?: string;
}

/** Sugestão da IA aguardando curadoria humana: nunca aplicada sem aceite. */
export function AISuggestion({
  title,
  evidence,
  confidence,
  onAccept,
  onEdit,
  onReject,
  acceptLabel = "Aceitar",
  busy,
  className,
}: AISuggestionProps) {
  return (
    <div className={cn("rounded-lg border border-dashed border-tone-ai-border bg-tone-ai-bg/40 p-3 text-sm", className)}>
      <div className="flex flex-wrap items-center gap-2">
        <AIBadge confidence={confidence} />
        <span className="font-semibold">{title}</span>
      </div>
      {evidence && <p className="mt-1.5 text-xs text-muted-foreground">{evidence}</p>}
      {(onAccept || onEdit || onReject) && (
        <div className="mt-2.5 flex gap-2">
          {onAccept && (
            <Button size="sm" onClick={onAccept} disabled={busy}>
              {acceptLabel}
            </Button>
          )}
          {onEdit && (
            <Button size="sm" variant="outline" onClick={onEdit} disabled={busy}>
              Editar
            </Button>
          )}
          {onReject && (
            <Button size="sm" variant="ghost" onClick={onReject} disabled={busy}>
              Descartar
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
